/**
 * **Raw data + export** — `APPLICATION_SPEC.md` §5.2, SOW workstream 6, rebuilt
 * 11 Sep to the AG Parts direction (RD-9…RD-14).
 *
 * > *"Set up like the Donovan app · significantly less filters · show preview of
 * > data · a lot less clicks to download · Filter, Preview and Click-to-Download
 * > should all be on the first page of the exports section and different
 * > datasets should show as tabs."*
 *
 * ### What changed, and why each part
 *
 * **Two tab levels, not one.** Fourteen datasets do not fit a single row at
 * laptop width. The primary row is the *source* — Snapchat · ClickUp · This App
 * — which is also the one thing about a dataset a reader cannot infer from its
 * name: whether a figure came from Snap, from ClickUp, or from something typed
 * into this app. The secondary row is the datasets inside it, eight at most.
 * Donovan does exactly this with connectors and sub-tabs.
 *
 * **The table is the page, not a disclosure.** It used to sit behind a
 * *Preview* button, which is a click between the reader and the thing they came
 * for. It is now what renders under the tab, server-paginated and sortable —
 * because a preview you cannot order is a preview you cannot check a figure in.
 *
 * **Download is always one click.** `Export CSV` sits in the table's own header
 * bar, never behind a disclosure, and `Export All {Source}` produces a single
 * workbook of every permitted dataset in the active source.
 *
 * **No filters were added.** The date range is the only one, which is already
 * the minimum — *"significantly less filters"* here means *do not add any*.
 * Recorded so a later reader does not read the absence as an oversight.
 *
 * ### Two things that are load-bearing rather than cosmetic
 *
 * **The tab list is permission-filtered on the server.** A non-admin (revenue is
 * Admin-only since RV-3) is never offered revenue — not offered-then-refused, which
 * would still advertise that the figures are there. A source whose datasets are
 * all denied loses its tab entirely rather than rendering empty.
 *
 * **Every caveat travels with its dataset.** An export is where a caveat is most
 * likely to be dropped: a CSV of `profile_stats_daily` looks authoritative and
 * will happily let someone add channel `VIEWS` to `STORY_VIEWS` — two different
 * counting bases, ~14x apart. So the trap sits on the dataset, next to the
 * download.
 */
import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { CustomFieldsPanel, type CustomFieldsTarget } from '../components/CustomFieldsPanel'
import { bodyCell, headCell } from '../components/DataTable'
import { DownloadButton } from '../components/DownloadButton'
import { InfoDot } from '../components/InfoDot'
import { RawDataFilters } from '../components/RawDataFilters'
import { Tabs } from '../components/Tabs'
import { Button, Card, Note, Skeleton, Tag } from '../components/ui'
import { api, type DatasetCard, type RawFilters } from '../lib/api'
import { useFilters } from '../lib/filters'
import { formatNumber } from '../lib/numbers'
import { readRawFilters, writeRawFilters } from '../lib/rawFilters'
import { ViewHeader } from '../shell/ViewHeader'

/** Snap · ClickUp · this app. The order data flows in, and the same order the
 *  server builds `export-all` from. */
const SOURCES = [
  { key: 'snap', label: 'Snapchat', blurb: 'Pulled Nightly from the Public Profile API' },
  { key: 'clickup', label: 'ClickUp', blurb: 'Episode Number, Writer and Re-Run Flag' },
  // 🔴 **`This App` used to promise two datasets that are not in it.** It read
  // *"Revenue You Typed, Users, and Pipeline History"*, and the tab holds
  // Revenue, the Revenue Change Record and the Channel Registry — there is no
  // users dataset and no run-history dataset anywhere in `/raw-data`
  // (`api/raw_data.py` `DATASETS` is the list). A blurb naming tables the tab
  // does not contain is worse than a vague one: it sends someone looking.
  { key: 'app', label: 'This App', blurb: 'Revenue and the Channel Registry' },
] as const

type SourceKey = (typeof SOURCES)[number]['key']

const PAGE_SIZE = 50

/** Only what this dataset answers — the server refuses the rest with a 400,
 *  and the panel shows the rest greyed out rather than sending it. */
function applicableTo(card: DatasetCard, filters: RawFilters): RawFilters {
  const keys = new Set(card.filters.map((spec) => spec.key))
  return Object.fromEntries(Object.entries(filters).filter(([key]) => keys.has(key)))
}

function DatasetTable({
  card,
  from,
  to,
  filters: allFilters,
  onDataset,
}: {
  card: DatasetCard
  from: string
  to: string
  filters: RawFilters
  /** Switch dataset, keeping the filters — the empty-daily fallback uses it. */
  onDataset?: (key: string) => void
}) {
  const filters = useMemo(() => applicableTo(card, allFilters), [card, allFilters])
  const filterKey = JSON.stringify(filters)
  const dated = card.date_column !== null
  const [page, setPage] = useState(0)
  const [sort, setSort] = useState<{ column: string; direction: 'asc' | 'desc' } | null>(null)
  // ST-27c: the ClickUp task whose custom fields are open in the panel.
  const [fieldsOf, setFieldsOf] = useState<CustomFieldsTarget | null>(null)

  // A new dataset, or a new range, invalidates the page you were on — page 7 of
  // a table you have just left is not a meaningful place to land.
  useEffect(() => setPage(0), [card.key, from, to, sort, filterKey])

  const range = dated ? { from, to } : {}
  const preview = useQuery({
    queryKey: ['dataset-page', card.key, from, to, filterKey, page, sort?.column, sort?.direction],
    queryFn: () =>
      api.datasetPreview(card.key, {
        ...range,
        page,
        pageSize: PAGE_SIZE,
        sort: sort?.column ?? null,
        direction: sort?.direction,
        filters,
      }),
    // Keeps the previous page on screen while the next one loads, so paging
    // does not flash the table away and back.
    placeholderData: (previous) => previous,
  })

  const data = preview.data
  const sortable = useMemo(() => new Set(data?.sortable ?? []), [data?.sortable])
  const pages = data ? Math.max(1, Math.ceil(data.row_count / data.page_size)) : 1

  const toggleSort = (column: string) => {
    if (!sortable.has(column)) return
    setSort((current) =>
      current?.column === column
        ? { column, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { column, direction: 'desc' },
    )
  }

  return (
    <Card
      title={card.label}
      // **The dot says what the table is, not what to watch out for.**
      // It used to carry the counting-basis traps and the source caveats —
      // written for us. This screen is read by someone outside the project who
      // wants to know whether this is the dataset they came for. The warnings
      // moved to `docs/CLIENT-ANSWERS-2026-09-11.md` §4, sent once.
      info={card.description || undefined}
      action={
        <div className="flex items-center gap-3">
          <span className="text-xs" style={{ color: 'var(--text-label)' }}>
            {/* 🔴 The catalogue's count honours the date range and nothing else,
                so under a filter it read "480,092 rows" above a table of two
                stories' rows. The filtered page's own count wins once loaded. */}
            {data ? formatNumber(data.row_count) : '…'}{' '}
            {data?.row_count === 1 ? 'row' : 'rows'}{' '}
            {Object.keys(filters).length
              ? 'matching filters'
              : dated
                ? 'in range'
                : 'all'}{' '}
            ·{' '}
            <span className="is-mono">
              {card.schema_name}.{card.table}
            </span>
          </span>
          {/* **`Restricted`, not the permission's name — 17 Sep.** This read
              `View Revenue`, which is the grant that gates the dataset, and the
              catalogue is permission-filtered server-side (`_visible_to`) — so
              the tag could only ever name a permission the reader already
              holds. What it is actually for is the other thing: telling whoever
              is about to export a revenue CSV that not everyone on this screen
              sees it. One word says that; the grant name said it sideways. */}
          {card.requires && <Tag tone="brand">Restricted</Tag>}
          {/* Always visible. The AG Parts note was "a lot less clicks to
              download", and a download behind a disclosure is the click.

              RD-17: a button rather than an anchor, so the page can say the
              export is working. `lib/download.ts` has the reasoning. */}
          <DownloadButton
            // RD-20: the file is the table on screen, filters and all.
            url={api.datasetExportUrl(card.key, { ...range, filters })}
            filename={`${card.key}.csv`}
            className="px-3 py-2 text-sm font-semibold"
            style={{
              background: 'var(--brand-ink)',
              color: 'var(--bg-surface)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
            }}
          >
            Export CSV
          </DownloadButton>
        </div>
      }
    >
      {preview.isLoading && !data ? (
        <Skeleton rows={6} label="Loading Rows…" />
      ) : preview.error ? (
        <Note tone="error">{(preview.error as Error).message}</Note>
      ) : !data?.rows.length ? (
        card.key === 'asset_stats_daily' && filters.assets?.length ? (
          // RD-21's safety net. Snap returns some posts in its lifetime totals
          // and never in its daily figures — measured 25 Sep, 114 posts inside
          // the 90-day window, mostly saved stories and older public stories —
          // so an empty table here is Snap's answer, not a filter mistake.
          <Note>
            Snapchat Returned No Daily Figures for{' '}
            {filters.assets.length === 1 ? 'This Post' : 'These Posts'} in This Range. Its
            Lifetime Totals Are Usually Still Available.{' '}
            {onDataset && (
              <Button variant="quiet" onClick={() => onDataset('asset_stats_lifetime')}>
                Open Asset Lifetime Stats
              </Button>
            )}
          </Note>
        ) : (
          <Note>
            {Object.keys(filters).length ? 'No Rows Match These Filters.' : 'No Rows in This Range.'}
          </Note>
        )
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table className="w-full" style={{ borderCollapse: 'collapse' }}>
              <caption className="sr-only">
                {card.label}, page {data.page + 1} of {pages}
              </caption>
              <thead>
                <tr>
                  {data.columns.map((column) => {
                    const canSort = sortable.has(column)
                    const active = sort?.column === column
                    return (
                      <th
                        key={column}
                        scope="col"
                        aria-sort={
                          active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'
                        }
                        style={{ ...headCell, padding: 0 }}
                      >
                        {/* A real button, not a `<th onClick>` — the design
                            system's checklist requires keyboard reachability,
                            and a click handler on a header is mouse-only. An
                            unsortable column gets no button, because a control
                            that does nothing is worse than no control. */}
                        {canSort ? (
                          <button
                            type="button"
                            onClick={() => toggleSort(column)}
                            className="w-full px-2 py-2"
                            style={{
                              background: 'transparent',
                              border: 'none',
                              color: 'inherit',
                              font: 'inherit',
                              letterSpacing: 'inherit',
                              textTransform: 'inherit',
                              textAlign: 'left',
                              cursor: 'pointer',
                            }}
                          >
                            {column}
                            {active && (sort.direction === 'asc' ? ' ▲' : ' ▼')}
                          </button>
                        ) : (
                          <span className="block px-2 py-2">{column}</span>
                        )}
                      </th>
                    )
                  })}
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row, index) => (
                  <tr key={index}>
                    {row.map((value, column) => {
                      // ST-27c (Nilay, 13 Oct): `custom_fields` on ClickUp Tasks
                      // only. Every other long cell keeps its hover text.
                      const opensFields =
                        value !== null &&
                        card.key === 'clickup_tasks' &&
                        data.columns[column] === 'custom_fields'
                      return (
                      <td
                        key={column}
                        style={{
                          ...bodyCell,
                          maxWidth: '18rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                        className={
                          value !== null && /^[\d.-]+$/.test(value)
                            ? 'is-num text-xs'
                            : 'text-xs'
                        }
                        // **The whole value on hover, for the wide ones.**
                        // `custom_fields` averages ~436 characters and every
                        // ClickUp field lives in it, so the cell has to
                        // truncate — but truncating without a way to read the
                        // rest makes the column decorative. The native `title`
                        // is right here: this is overflow, not a caveat, and
                        // mounting a tooltip component on every cell of a
                        // 50-row × 18-column table is hundreds of listeners for
                        // something the browser already does. The export has the
                        // full value either way.
                        title={
                          !opensFields && value !== null && value.length > 60 ? value : undefined
                        }
                      >
                        {opensFields ? (
                          <button
                            type="button"
                            className="block w-full text-left"
                            style={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              background: 'none',
                              border: 'none',
                              padding: 0,
                              color: 'var(--brand-ink)',
                              cursor: 'pointer',
                              font: 'inherit',
                            }}
                            title="Open All Custom Fields"
                            onClick={() =>
                              setFieldsOf({
                                taskId: row[data.columns.indexOf('id')] ?? '',
                                name: row[data.columns.indexOf('name')] ?? null,
                                raw: value,
                              })
                            }
                          >
                            {value}
                          </button>
                        ) : value === null ? (
                          // Not zero, not blank — an em dash, because a null in
                          // this app means "not recorded" and an empty cell
                          // loses that.
                          <span style={{ color: 'var(--text-label)' }}>—</span>
                        ) : (
                          value
                        )}
                      </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {fieldsOf && <CustomFieldsPanel target={fieldsOf} onClose={() => setFieldsOf(null)} />}

          <div className="mt-3 flex items-center gap-3">
            <Button variant="quiet" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}>
              ‹ Previous
            </Button>
            <span className="text-xs" style={{ color: 'var(--text-label)' }}>
              Page {data.page + 1} of {formatNumber(pages)} · {formatNumber(data.row_count)}{' '}
              {data.row_count === 1 ? 'row' : 'rows'}
            </span>
            <Button
              variant="quiet"
              onClick={() => setPage((p) => p + 1)}
              disabled={page + 1 >= pages}
            >
              Next ›
            </Button>
          </div>
        </>
      )}
    </Card>
  )
}

export function RawData() {
  const { from, to } = useFilters()
  const datasets = useQuery({
    // No counts, and so no range in the key: the list is the same for every
    // range, loads once, and a range change no longer refetches it.
    queryKey: ['datasets'],
    queryFn: () => api.datasets(),
    // 🔴 **X-16 — this is the bug Abi reported as a calendar bug.**
    //
    // *"when I try to customize the date range and I click on one of the arrows
    // in the calendar function to scroll to a different month, it blanks out the
    // entire page temporarily."*
    //
    // The range is in this key, so any change refetches; the page then returned
    // a skeleton **in place of everything, `ViewHeader` included** — and the
    // date `<input>` lives inside `ViewHeader`. So the control the reader was
    // interacting with was destroyed mid-interaction, taking the browser's
    // native picker with it. It reads as the calendar breaking the page because
    // from the reader's side that is exactly what happened.
    //
    // Keeping the previous list means the tab row and the header survive a range
    // change. `DatasetTable` already did this for the rows inside it, which is
    // why only the outer frame flickered.
    placeholderData: (previous) => previous,
  })

  const cards = useMemo(() => datasets.data ?? [], [datasets.data])

  // RD-13. A source whose datasets are all denied loses its tab rather than
  // rendering empty — an empty tab still says the data is there.
  const sources = useMemo(
    () => SOURCES.filter((source) => cards.some((card) => card.schema_name === source.key)),
    [cards],
  )

  // RD-20: the dataset and its filters live in the URL, so a click-through
  // from Stories or Spotlights can land on exactly one story's rows, and a
  // shared link reproduces the view.
  const [params, setParams] = useSearchParams()
  const filters = useMemo(() => readRawFilters(params), [params])
  const dataset = params.get('dataset')
  const setDataset = (next: string | null) =>
    setParams(
      (current) => {
        const updated = new URLSearchParams(current)
        if (next) updated.set('dataset', next)
        else updated.delete('dataset')
        return updated
      },
      { replace: true },
    )
  const setFilters = (next: RawFilters) =>
    setParams((current) => writeRawFilters(current, next), { replace: true })

  const [source, setSource] = useState<SourceKey>('snap')
  // A linked dataset decides the source tab, not the other way round.
  const linked = cards.find((entry) => entry.key === dataset)
  useEffect(() => {
    if (linked) setSource(linked.schema_name as SourceKey)
  }, [linked])

  const active = sources.find((entry) => entry.key === source) ?? sources[0]
  const inSource = useMemo(
    () => cards.filter((card) => card.schema_name === active?.key),
    [cards, active],
  )
  const card = inSource.find((entry) => entry.key === dataset) ?? inSource[0]

  // Keep the selection valid as permissions or the source change, rather than
  // rendering nothing when the remembered dataset is not in this source.
  useEffect(() => {
    if (active && active.key !== source) setSource(active.key)
  }, [active, source])

  // **Every state renders the header.** The three below are the page's own
  // loading, error and no-access answers, and each used to `return` before
  // `ViewHeader` existed on screen — which is what made a refetch look like the
  // page disappearing rather than a panel updating.
  if (datasets.isLoading || datasets.error || cards.length === 0) {
    return (
      <div>
        <ViewHeader title="Raw Data" channelPicker={false} />
        {datasets.isLoading ? (
          <Skeleton rows={6} label="Loading Datasets…" />
        ) : datasets.error ? (
          <Note tone="error">Could Not Load the Dataset List.</Note>
        ) : (
          <Note>You Do Not Have Access to Any Dataset. An Administrator Grants These.</Note>
        )}
      </div>
    )
  }

  return (
    <div>
      <ViewHeader
        title="Raw Data"
        status={`${cards.length} datasets`}
        channelPicker={false}
        views={sources.map((entry) => ({ key: entry.key, label: entry.label }))}
        activeView={active?.key ?? 'snap'}
        onView={(next) => {
          setSource(next as SourceKey)
          setDataset(null)
        }}
      >
        {/* The source blurb and the whole-source export ride on the filter row
            rather than each claiming a line. Two short things, and the row had
            room — the separate line was mostly whitespace. */}
        <span className="text-xs" style={{ color: 'var(--text-label)' }}>
          {active?.blurb}
        </span>
        {active && (
          <span className="ml-auto inline-flex items-center gap-1">
            {/* The workbook is the slowest export in the app — every permitted
                dataset in the source, built sheet by sheet — so it is the one
                that most needed RD-17. */}
            <DownloadButton
              url={api.datasetExportAllUrl(active.key, { from, to, filters })}
              filename={`${active.key}-export.xlsx`}
              className="px-3 py-2 text-sm font-medium"
              style={{
                background: 'transparent',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-md)',
                color: 'var(--text-primary)',
              }}
            >
              Export All {active.label}
            </DownloadButton>
            {/* Short. The truncation and slow-range caveats were true and
                belonged nowhere near a button label — the workbook says so in
                its own sheet if it ever applies. */}
            <InfoDot note="One Excel Workbook, One Sheet per Dataset." />
          </span>
        )}
      </ViewHeader>

      {inSource.length > 0 && (
        <Tabs
          tabs={inSource.map((entry) => ({ key: entry.key, label: entry.label }))}
          active={card?.key ?? inSource[0]!.key}
          onChange={setDataset}
          label="Dataset"
        />
      )}

      {card && <RawDataFilters card={card} filters={filters} onChange={setFilters} />}
      {card && (
        <DatasetTable card={card} from={from} to={to} filters={filters} onDataset={setDataset} />
      )}
    </div>
  )
}
